import React, { useState } from "react";
import { Modal } from "reactstrap";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Image,
  Font,
} from "@react-pdf/renderer";
import axios from "axios";
import configure from "configure";
import Loader from "pages/Separate/Loader";
import zithasLogo from "../../../assets/images/zithas-dark-logo.png";
import GenerateEmailModal from "./GenerateEmailModal";

// ─── PDF Styles — matching the Word document exactly ──────────────────────

const S = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 56,
    paddingHorizontal: 56,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#000000",
    lineHeight: 1.45,
  },

  // ── Logo ──
  logoWrap: {
    alignItems: "center",
    marginBottom: 14,
  },
  logo: {
    width: 130,
    height: "auto",
  },

  // ── Cover title block ──
  titleWrap: {
    alignItems: "center",
    marginBottom: 2,
  },
  title: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    textDecoration: "underline",
    color: "#000000",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textDecoration: "underline",
    color: "#000000",
    textAlign: "center",
    marginTop: 2,
    marginBottom: 20,
  },

  // ── Prepared by ──
  preparedLabel: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    marginBottom: 0,
  },
  preparedValue: {
    fontSize: 10,
    color: "#000000",
    marginBottom: 16,
  },

  // ── Section heading (bold, left-aligned) ──
  sectionHead: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    marginTop: 12,
    marginBottom: 4,
  },
  sectionHeadUpper: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    marginTop: 12,
    marginBottom: 4,
    textTransform: "uppercase",
  },

  // ── Category heading (larger bold — "Mobile App Features") ──
  categoryHead: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    marginTop: 14,
    marginBottom: 3,
  },

  // ── Group heading ("1. Medicine Ordering & E-commerce") ──
  groupHead: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    marginTop: 8,
    marginBottom: 1,
  },

  // ── Paragraph ──
  para: {
    fontSize: 10,
    color: "#000000",
    lineHeight: 1.45,
    marginBottom: 6,
  },

  // ── Bullet row (✓ tick) ──
  bulletRow: {
    flexDirection: "row",
    marginBottom: 1,
  },
  bulletTick: {
    fontSize: 10,
    color: "#000000",
    width: 12,
    flexShrink: 0,
  },
  bulletText: {
    fontSize: 10,
    color: "#000000",
    flex: 1,
    lineHeight: 1.45,
  },
  bulletTextBold: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
  },

  // ── Nested bullet (indent level 1) ──
  bulletRowIndent: {
    flexDirection: "row",
    marginBottom: 1,
    paddingLeft: 16,
  },

  // ── Yellow highlight (cost line) ──
  highlightWrap: {
    marginBottom: 12,
    paddingLeft: 6,
  },
  highlightText: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    backgroundColor: "#FFFF00",
    color: "#000000",
  },

  // ── Next Steps plain lines ──
  plainLine: {
    fontSize: 10,
    color: "#000000",
    marginBottom: 0,
  },
});

// ─── Reusable components ────────────────────────────────────────────────────

const Tick = ({ children, indent = false }) => (
  <View style={indent ? S.bulletRowIndent : S.bulletRow}>
    <Text style={S.bulletTick}>•</Text>
    {/* <Text style={S.bulletTick}>✓</Text> */}
    <Text style={S.bulletText}>{children}</Text>
  </View>
);

// ─── The PDF Document ───────────────────────────────────────────────────────

const ProposalPDFDoc = ({ proposalData, rfpData }) => {
  const companyName = proposalData?.companyname || "";
  const projectTitle = proposalData?.proposaltitile || "Proposal Document";
  const currencySymbol = rfpData?.currency_symbol || "";
  const grandTotal = rfpData?.grand_total || null;
  const vatPct = rfpData?.vat?.pct || null;
  const vatAmount = rfpData?.vat?.amount || null;
  const subTotalBeforeVat =
    grandTotal && vatAmount ? (grandTotal - vatAmount).toFixed(2) : null;

  //   const rfpFeatureRows = (() => {
  //   if (!rfpData?.features || !Array.isArray(rfpData.features)) return [];
  //   const rows = [];
  //   rfpData.features.forEach((svc) => {
  //     (svc.subtitles || []).forEach((sub) => {
  //       rows.push({
  //         num: String(rows.length + 1),
  //         title: sub.title || "",
  //         items: (sub.features || []).map(f => f.title || "").filter(Boolean),
  //       });
  //     });
  //   });
  //   return rows;
  // })();

  // const hasRfpFeatures = rfpFeatureRows.length > 0;

  // ── Build features grouped by service (with service name) ──
  const rfpFeatureGroups = (() => {
    if (!rfpData?.features || !Array.isArray(rfpData.features)) return [];

    // Get services list for name lookup
    const services = (() => {
      try {
        const raw = rfpData.services;
        return typeof raw === "string" ? JSON.parse(raw) : raw || [];
      } catch {
        return [];
      }
    })();

    return rfpData.features.map((svc) => {
      // Find service name
      const svcName =
        services.find(
          (s) =>
            String(s.value) === String(svc.serviceId) ||
            String(s.id) === String(svc.serviceId),
        )?.label ||
        services.find((s) => String(s.id) === String(svc.serviceId))?.service ||
        `Service ${svc.serviceId}`;

      return {
        serviceName: svcName,
        subtitles: (svc.subtitles || []).map((sub, si) => ({
          num: si + 1,
          title: sub.title || "",
          items: (sub.features || []).map((f) => f.title || "").filter(Boolean),
        })),
      };
    });
  })();

  const hasRfpFeatures = rfpFeatureGroups.length > 0;

  const phases = (() => {
    try {
      const saved =
        typeof proposalData?.timeline === "string"
          ? JSON.parse(proposalData.timeline)
          : proposalData?.timeline;
      if (Array.isArray(saved)) {
        return saved.map((p, i) => ({
          num: String(i + 1),
          title: p.phase || "",
          duration: p.duration ? `Wk ${p.duration}` : "",
          desc: p.deliverables || "",
        }));
      }
    } catch {}
    return [];
  })();

  // ── Terms data for duration/cost ──
  const termsData = (() => {
    try {
      const raw = proposalData?.terms;
      return typeof raw === "string" ? JSON.parse(raw) : raw || [];
    } catch {
      return [];
    }
  })();
  const termsDuration = termsData[0]?.value || termsData[0]?.total || "";

  // ── Parse cost breakdown ──
  const breakdownData = (() => {
    try {
      const raw = proposalData?.costbreakdown;
      return typeof raw === "string" ? JSON.parse(raw) : raw || [];
    } catch {
      return [];
    }
  })();

  // ── Parse project objective ──
  const projectObjectiveText = proposalData?.project_objective || "";

  // ── Currency symbol from terms ──
  const currSymbol = termsData[1]?.currency || termsData[0]?.currency || "";

  return (
    <Document>
      <Page size="A4" style={S.page}>
        {/* ── LOGO ── */}
        <View style={S.logoWrap}>
          <Image style={S.logo} src={zithasLogo} />
        </View>

        {/* ── TITLE ── */}
        <View style={S.titleWrap}>
          <Text style={S.title}>Proposal Document</Text>
          <Text style={S.subtitle}>{projectTitle}</Text>
        </View>

        {/* ── PREPARED BY ── */}
        <Text style={S.preparedLabel}>Prepared By:</Text>
        <Text style={S.preparedValue}>Zithas Technologies</Text>

        {/* ══════════════════════════════════════════════════════════════════
            1. PROJECT OBJECTIVE
        ══════════════════════════════════════════════════════════════════ */}
        <Text style={S.sectionHead}>1. Project objective</Text>

        {/* <Text style={S.para}>
          In today's fast-evolving healthcare and pharmaceutical landscape,
          having a centralized digital platform is essential for managing
          services, customers, and operations efficiently. Quick Pharma
          Services is planning to develop a modern mobile application and
          website to streamline service delivery, improve customer engagement,
          and enhance overall operational efficiency.
        </Text>
        <Text style={S.para}>
          This initiative focuses on creating a secure, scalable, and
          user-friendly digital ecosystem that connects customers,
          administrators, and service teams through a single platform.
        </Text>

        <Tick>Mobile App &amp; Website Development for Quick Pharma Services</Tick>
        <Tick>Dashboard &amp; Order Tracking</Tick>
        <Tick>Notifications &amp; Real-Time Updates</Tick>
        <Tick>Secure User Login &amp; Admin Control Panel</Tick>
        <Tick>Integrated Payment &amp; Billing Management</Tick>

        <Text style={[S.para, { marginTop: 6 }]}>
          The strategy aims to build a unified platform that reduces manual
          work, improves accuracy, and enables Quick Pharma Services to
          efficiently manage customers, orders, services, and communications
          from one centralized system. This will enhance user experience,
          increase operational efficiency, and support future scalability of
          the business.
        </Text> */}
        {projectObjectiveText ? (
          projectObjectiveText.split("\n").map((line, i) => {
            const trimmed = line.trim();
            if (!trimmed) return null;
            if (trimmed.startsWith("•")) {
              return <Tick key={i}>{trimmed.slice(1).trim()}</Tick>;
            }
            return (
              <Text key={i} style={S.para}>
                {trimmed}
              </Text>
            );
          })
        ) : (
          <Text style={S.para}>
            {companyName} requires a scalable, secure digital platform to
            streamline operations and enhance user experience.
          </Text>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            2. FEATURE LIST
        ══════════════════════════════════════════════════════════════════ */}
        <Text style={S.sectionHeadUpper}>2. FEATURE LIST</Text>

        {/* {hasRfpFeatures ? (
  // Dynamic from RFP
  rfpFeatureRows.map((section, i) => (
    <View key={i}>
      <Text style={S.groupHead}>{section.num}. {section.title}</Text>
      {section.items.map((item, j) => (
        <Tick key={j}>{item}</Tick>
      ))}
    </View>
  ))
) : ( */}

        {hasRfpFeatures ? (
          rfpFeatureGroups.map((group, gi) => (
            <View key={gi}>
              {/* Service name — like "Mobile App Features" */}
              <Text style={S.categoryHead}>{group.serviceName}</Text>

              {/* Subtitles + their sub-features */}
              {group.subtitles.map((sub, si) => (
                <View key={si}>
                  <Text style={S.groupHead}>
                    {sub.num}. {sub.title}
                  </Text>
                  {sub.items.map((item, ii) => (
                    <Tick key={ii}>{item}</Tick>
                  ))}
                </View>
              ))}
            </View>
          ))
        ) : (
          // Static fallback — ALL your existing hardcoded content stays here unchanged
          <>
            {/* ─── Mobile App Features ── */}
            <Text style={S.categoryHead}>Mobile App Features</Text>

            <Text style={S.groupHead}>
              1. Medicine Ordering &amp; E-commerce
            </Text>
            <Tick>Upload prescription and get call back</Tick>
            <Tick>
              Buy products (healthcare, personal care, baby care, diabetes
              products)
            </Tick>
            <Tick>
              Assisted ordering (support team helps if prescription unclear)
            </Tick>

            <Text style={S.groupHead}>2. Delivery &amp; Fulfillment</Text>
            <Tick>Home delivery</Tick>
            <Tick>Click &amp; Pick (order online, collect from store)</Tick>
            <Tick>Real-time order processing by licensed pharmacists</Tick>

            <Text style={S.groupHead}>
              3. Medicine Discovery &amp; Information
            </Text>
            <Tick>
              Search medicines and check availability in nearby stores
            </Tick>
            <Tick>Detailed medicine info</Tick>

            <Text style={S.groupHead}>4. Store Locator &amp; Omni-channel</Text>
            <Tick>Locate nearest store</Tick>
            <Tick>Check stock availability at specific stores</Tick>

            <Text style={S.groupHead}>5. Health Management Tools</Text>
            <Tick>Pill reminder (medication alerts)</Tick>
            <Tick>Health records storage (upload prescriptions, reports)</Tick>
            <Tick>Order history tracking</Tick>
            <Tick>Previous prescriptions saved for reorders</Tick>

            <Text style={S.groupHead}>6. Loyalty &amp; Rewards System</Text>
            <Tick>Rewards program</Tick>
            <Tick>Earn points on purchases</Tick>
            <Tick>Redeem for discounts, products, or cash benefits</Tick>
            <Tick>Membership-based savings</Tick>

            <Text style={S.groupHead}>7. Billing &amp; Account Management</Text>
            <Tick>Digital invoices stored in-app</Tick>
            <Tick>Wallet / coupons / offers management</Tick>

            <Text style={S.groupHead}>8. Notifications &amp; Tracking</Text>
            <Tick>Real-time order tracking</Tick>
            <Tick>Alerts for:</Tick>
            <Tick indent>Delivery updates</Tick>
            <Tick indent>Medicine reminders</Tick>
            <Tick indent>Offers &amp; rewards</Tick>

            <Text style={S.groupHead}>9. Healthcare Services (Extended)</Text>
            <Tick>Option for doctor consultation (assisted flow)</Tick>

            <Text style={S.groupHead}>10. Prescription &amp; Compliance</Text>
            <Tick>Prescription upload for regulated medicines</Tick>
            <Tick>Ensures compliance with healthcare regulations</Tick>

            {/* ─── Website Features ── */}
            <Text style={S.categoryHead}>Website Features</Text>

            <Text style={S.groupHead}>1. Core Commerce</Text>
            <Tick>Medicine &amp; product browsing</Tick>
            <Tick>Prescription upload (file upload)</Tick>
            <Tick>Bulk ordering</Tick>

            <Text style={S.groupHead}>2. Advanced Search &amp; Discovery</Text>
            <Tick>Detailed filters:</Tick>
            <Tick indent>Brand / generic</Tick>
            <Tick indent>Price range</Tick>
            <Tick indent>Categories</Tick>
            <Tick>Compare medicines &amp; alternatives</Tick>

            <Text style={S.groupHead}>3. Detailed Information</Text>
            <Tick>Detailed product pages:</Tick>
            <Tick indent>Composition</Tick>
            <Tick indent>Side effects</Tick>
            <Tick indent>Usage guidelines</Tick>
            <Tick>Educational content (blogs)</Tick>

            <Text style={S.groupHead}>4. Account &amp; Order Management</Text>
            <Tick>Full order history dashboard</Tick>
            <Tick>Download invoices (PDF)</Tick>
            <Tick>Manage prescriptions</Tick>

            <Text style={S.groupHead}>5. Store &amp; Inventory</Text>
            <Tick>Store locator with map</Tick>
            <Tick>Check availability at specific stores</Tick>

            <Text style={S.groupHead}>6. Offers &amp; Membership</Text>
            <Tick>Loyalty program dashboard</Tick>
            <Tick>View reward history</Tick>
            <Tick>Apply coupons manually</Tick>

            {/* ─── Admin Panel Features ── */}
            <Text style={S.categoryHead}>Admin Panel Features</Text>

            <Text style={S.groupHead}>1. Dashboard &amp; Analytics</Text>
            <Tick>Sales overview</Tick>
            <Tick>Revenue &amp; profit tracking</Tick>
            <Tick>Orders summary (pending, delivered, cancelled)</Tick>
            <Tick>Top-selling products</Tick>
            <Tick>Inventory alerts (low stock, expiry warnings)</Tick>

            <Text style={S.groupHead}>2. User Management</Text>
            <Tick>View &amp; manage users</Tick>
            <Tick>Block / suspend users</Tick>
            <Tick>Access order history per user</Tick>

            <Text style={S.groupHead}>
              3. Product &amp; Medicine Management
            </Text>
            <Tick>Add / edit / delete medicines &amp; products</Tick>
            <Tick>Manage:</Tick>
            <Tick indent>SKU, batch number</Tick>
            <Tick indent>Composition</Tick>
            <Tick indent>Manufacturer details</Tick>
            <Tick indent>Pricing &amp; discounts</Tick>
            <Tick indent>Upload product images</Tick>
            <Tick indent>Categorization (OTC, prescription, wellness)</Tick>

            <Text style={S.groupHead}>4. Inventory &amp; Stock Management</Text>
            <Tick>Real-time stock updates</Tick>
            <Tick>Multi-store inventory tracking</Tick>
            <Tick>Low stock alerts</Tick>

            <Text style={S.groupHead}>5. Prescription Management</Text>
            <Tick>View uploaded prescriptions</Tick>
            <Tick>Assign to pharmacist for validation</Tick>
            <Tick>Approve / reject orders</Tick>
            <Tick>Maintain prescription records</Tick>
            <Tick>Flag suspicious or invalid prescriptions</Tick>

            <Text style={S.groupHead}>6. Order Management</Text>
            <Tick>View all orders (filter by status)</Tick>
            <Tick>Order lifecycle management</Tick>
            <Tick>Handle cancellations &amp; returns</Tick>
            <Tick>Refund processing</Tick>

            <Text style={S.groupHead}>7. Offers, Coupons &amp; Loyalty</Text>
            <Tick>Create discount campaigns</Tick>
            <Tick>Coupon management: % discount / flat discount</Tick>
            <Tick>Expiry rules</Tick>
            <Tick>Manage Rewards system</Tick>

            <Text style={S.groupHead}>8. Payments &amp; Billing</Text>
            <Tick>Track transactions (UPI, cards, wallets)</Tick>
            <Tick>Invoice generation</Tick>
            <Tick>Refund tracking</Tick>
            <Tick>Payment reconciliation reports</Tick>

            <Text style={S.groupHead}>9. Reports &amp; Compliance</Text>
            <Tick>GST / tax reports</Tick>
            <Tick>Sales &amp; revenue reports</Tick>
            <Tick>Inventory &amp; expiry reports</Tick>
            <Tick>Prescription audit logs</Tick>

            <Text style={S.groupHead}>
              10. Role-Based Access Control (RBAC)
            </Text>
            <Tick>Role based Permissions</Tick>
            <Tick>Audit logs</Tick>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            4. ESTIMATED COST — yellow highlighted
        ══════════════════════════════════════════════════════════════════ */}
        {/* <Text style={S.sectionHeadUpper}>4. Estimated Cost</Text>
        <View style={S.highlightWrap}>
          <Text style={S.highlightText}>
            Cost :  3,50,000 INR - 3,80,000 INR
          </Text>
        </View> */}
        {termsData.length > 0 && (
          <>
            <Text style={S.sectionHeadUpper}>3. Estimated Cost</Text>

            {(() => {
              // Sum all rows EXCEPT the first one (which has duration/time, not cost)
              const costRows = termsData.slice(1); // skip index 0
              const total = costRows.reduce((acc, row) => {
                const val = parseFloat(row.value ?? row.total ?? 0);
                return acc + (isNaN(val) ? 0 : val);
              }, 0);

              const sym = termsData[1]?.currency || currSymbol || "";

              if (total > 0) {
                return (
                  <View style={S.highlightWrap}>
                    <View style={{ alignSelf: "flex-start" }}>
                      <Text style={S.highlightText}>
                        Cost : {sym}
                        {total.toLocaleString("en-GB", {
                          minimumFractionDigits: 2,
                        })}
                      </Text>
                    </View>
                  </View>
                );
              }
              return null;
            })()}
          </>
        )}

        <Text style={S.sectionHeadUpper}>4. PROJECT TIMELINE</Text>
        <View style={S.highlightWrap}>
          <View style={{ alignSelf: "flex-start" }}>
            <Text style={S.highlightText}>
              Time : {termsData[0]?.value ?? termsData[0]?.total ?? ""}
            </Text>
          </View>
        </View>

        {/* ══════════════════════════════════════════════════════════════════
            5. WHY CHOOSE US — inline bold within sentences
        ══════════════════════════════════════════════════════════════════ */}
        <Text style={S.sectionHeadUpper}>5. Why Choose Us</Text>

        <View style={S.bulletRow}>
          <Text style={S.bulletTick}>•</Text>
          <Text style={S.bulletText}>
            Extensive experience in developing{" "}
            <Text style={S.bulletTextBold}>
              mobile apps and responsive websites
            </Text>{" "}
            for service-based or healthcare-related businesses.
          </Text>
        </View>
        <View style={S.bulletRow}>
          <Text style={S.bulletTick}>•</Text>
          <Text style={S.bulletText}>
            Proven expertise in{" "}
            <Text style={S.bulletTextBold}>online ordering systems</Text>,
            customer management, and real-time service workflows.
          </Text>
        </View>
        <View style={S.bulletRow}>
          <Text style={S.bulletTick}>•</Text>
          <Text style={S.bulletText}>
            Seamless integration of{" "}
            <Text style={S.bulletTextBold}>third-party tools</Text> such as
            payment gateways, CRM systems, notifications, and reporting
            dashboards.
          </Text>
        </View>
        <View style={S.bulletRow}>
          <Text style={S.bulletTick}>•</Text>
          <Text style={S.bulletText}>
            Secure and scalable architecture ensuring{" "}
            <Text style={S.bulletTextBold}>
              data privacy, reliability, and performance
            </Text>
            .
          </Text>
        </View>

        <Text style={[S.para, { marginTop: 8 }]}>
          Our focus is not only on building an app and website, but on creating
          a{" "}
          <Text style={{ fontFamily: "Helvetica-Bold" }}>
            unified digital ecosystem
          </Text>{" "}
          that simplifies service requests, improves operational efficiency, and
          enhances customer satisfaction. This platform should enable Quick
          Pharma Services to manage orders, customers, and services efficiently
          while delivering a seamless experience across mobile and web
          platforms.
        </Text>

        {/* ══════════════════════════════════════════════════════════════════
            6. NEXT STEPS — plain lines, no bullets
        ══════════════════════════════════════════════════════════════════ */}
        <Text style={S.sectionHeadUpper}>6. Next Steps</Text>
        <Text style={S.plainLine}>Review the proposal</Text>
        <Text style={S.plainLine}>Confirm the Project scope</Text>
        <Text style={S.plainLine}>Will share the contract</Text>
      </Page>
    </Document>
  );
};

// ─── Main export — button + modal, exactly like ProposalPdf1 ───────────────

const ProposalPdfBlank = ({ proposal_id }) => {
  const [viewData, setViewData] = useState(false);
  const [proposalData, setProposalData] = useState({});
  const [loading, setLoading] = useState(false);
  const [allEmails, setAllEmails] = useState([]);
  const [rfpData, setRfpData] = useState(null);

  const fetchProposalData = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("proposal_id", proposal_id);
    try {
      const response = await axios({
        method: "post",
        url: `${configure.nodeApi}/view_proposal`,
        data: formData,
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      });
      const d = response.data.data[0];
      setProposalData(d);

      if (d.related === "lead" && d.source_name && d.source_name !== "null") {
        try {
          const rfpRes = await axios.get(
            `${configure.nodeApi}/get_rfp_by_lead/${d.source_name}`,
            { headers: { Accept: "application/json" } },
          );
          if (rfpRes.data.success && rfpRes.data.data) {
            setRfpData(rfpRes.data.data);
          }
        } catch (err) {
          console.error("Failed to fetch RFP data:", err);
        }
      }

      const emails = new Set();
      if (d.provideremail) emails.add(d.provideremail);
      if (d.related === "lead" && d.source_name && d.source_name !== "null") {
        try {
          const leadRes = await axios.get(
            `${configure.nodeApi}/lead_emails?lead_id=${d.source_name}`,
            { headers: { Accept: "application/json" } },
          );
          if (leadRes.data.success && Array.isArray(leadRes.data.data)) {
            leadRes.data.data.forEach((email) => {
              if (email) emails.add(email);
            });
          }
        } catch (err) {
          console.error("Failed to fetch lead emails:", err);
        }
      } else {
        if (d.email) emails.add(d.email);
      }
      setAllEmails([...emails]);
      setViewData(true);
    } catch (err) {
      console.error("fetchProposalData error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}

      {/* ── PDF icon button — same as ProposalPdf1 ── */}
      <button
        className="btn btn-sm btn-outline-secondary me-1"
        onClick={fetchProposalData}
        title="View Proposal PDF"
      >
        <i className="fa fa-file-pdf"></i>
      </button>

      {/* ── Modal — same structure as ProposalPdf1 ── */}
      <Modal size="xl" centered isOpen={viewData} className="modal-fullscreen">
        <div className="modal-header">
          <h5 className="modal-title mt-0">Proposal Preview</h5>
          <div className="d-flex align-items-center ms-auto gap-2">
            {/* <button
              className="btn btn-sm btn-outline-primary"
              title="Send via Outlook"
              onClick={() => {
                const to = encodeURIComponent(allEmails.join(";"));
                const subject = encodeURIComponent(
                  proposalData?.proposaltitile || "Proposal",
                );
                window.open(
                  `https://outlook.cloud.microsoft/mail/deeplink/compose?to=${to}&subject=${subject}`,
                  "_blank",
                );
              }}
            >
              <i className="fa fa-envelope me-1"></i> Send Email
              {allEmails.length > 0 && (
                <span className="badge bg-secondary ms-1">
                  {allEmails.length}
                </span>
              )}
            </button> */}
            <GenerateEmailModal
              proposalData={proposalData}
              rfpData={rfpData}
              allEmails={allEmails}
            />
            <button
              type="button"
              onClick={() => setViewData(false)}
              className="btn-modal-close"
              style={{ marginTop: "8px" }}
            >
              <span aria-hidden="true" className="btn-modal-span">
                &times;
              </span>
            </button>
          </div>
        </div>
        <div className="modal-body p-0">
          <PDFViewer width="100%" height="860px" style={{ border: "none" }}>
            <ProposalPDFDoc proposalData={proposalData} rfpData={rfpData} />
          </PDFViewer>
        </div>
      </Modal>
    </>
  );
};

export default ProposalPdfBlank;
export { ProposalPDFDoc };
